import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiArrowLeft, FiLoader } from 'react-icons/fi';

import {
  useStartABTest,
  useABTestSession,
  useApplyABTestVariant,
} from '../../hooks/useABTest';
import {
  useGenerateSuggestions,
  useSnapshot,
  useStartTraining,
} from '../../hooks/useAgentTraining';
import type { ABTestVariantInput } from '../../services/abTestApi';
import {
  agentTrainingApi,
  type ApplyTrainingResponse,
  type PromptSuggestion,
  type TrainingScenarioType,
} from '../../services/agentTrainingApi';
import { PromptDiffModal } from '../PromptDiffModal';

import { ABTestConfigStep } from './ABTestConfigStep';
import { ABTestResultsTable } from './ABTestResultsTable';
import { TrainStep1_ChooseType } from './TrainStep1_ChooseType';
import { TrainStep2_Running } from './TrainStep2_Running';
import { TrainStep3_Results } from './TrainStep3_Results';

/** Estados da state machine interna do modal. */
type ModalStep =
  | 'choose-type'      // Step 1
  | 'running'          // Step 2 — treino em progresso (polling)
  | 'results'          // Step 3 — resultado + sugestões + campo livre
  | 'diff'             // Step 4 — PromptDiffModal sobreposto
  | 'ab-config'        // A/B Testing — configuração de variantes
  | 'ab-running'       // A/B Testing — execução em progresso (polling)
  | 'ab-results';      // A/B Testing — tabela de resultados

interface AgentTrainModalProps {
  isOpen: boolean;
  agentId: string | undefined;
  /**
   * Chamado quando o usuário confirma o diff. Recebe o novo systemPrompt
   * a ser aplicado no formulário do agente (ainda não salva) + o ID do
   * snapshot que originou a mudança. O AgentFormPage guarda esse ID
   * como `pendingBaseSnapshotId` e envia no body do update quando o
   * cliente clicar em "Salvar" — assim a nova versão fica vinculada ao
   * treino via `baseSnapshotId`.
   */
  onApplyPrompt: (newSystemPrompt: string, baseSnapshotId: string) => void;
  /** Chamado quando o modal é fechado sem aplicar nada. */
  onClose: () => void;
}

/**
 * Modal de treinamento do agente — fluxo de 4 passos unificado.
 *
 * Substitui o antigo `TrainAgentModal.tsx` simples (que fazia 1 textarea
 * → IA reescreve → diff). O fluxo novo é data-driven: executa smoke test,
 * gera sugestões via Prompt Coach, permite campo livre, aplica via diff.
 *
 * ## State machine
 *
 * ```
 * choose-type ──(select)──▶ running ──(completed)──▶ results
 *                   │                                    │
 *                   │  (manual mode)                     │  (clica "Aplicar")
 *                   └──────────────▶ results             ▼
 *                                                       diff
 *                                                        │
 *                                                  ┌─────┴─────┐
 *                                                  │           │
 *                                            (rejeita)   (confirma)
 *                                                  │           │
 *                                                  ▼           ▼
 *                                              results    onApplyPrompt
 *                                                          + close
 * ```
 */
export const AgentTrainModal: React.FC<AgentTrainModalProps> = ({
  isOpen,
  agentId,
  onApplyPrompt,
  onClose,
}) => {
  // ─── State ────────────────────────────────────────────────────────────
  const [step, setStep] = useState<ModalStep>('choose-type');
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<string[]>(
    [],
  );
  const [userInstructions, setUserInstructions] = useState('');
  const [suggestions, setSuggestions] = useState<PromptSuggestion[]>([]);
  const [suggestionsError, setSuggestionsError] = useState<Error | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyTrainingResponse | null>(
    null,
  );
  /**
   * Mensagem de erro específica do "apply" (Step 3 → Step 4). Fica no
   * Step 3 até o cliente tentar de novo. Separada de `suggestionsError`
   * porque são fases diferentes do fluxo.
   */
  const [applyError, setApplyError] = useState<string | null>(null);
  /**
   * Texto do novo prompt sendo recebido via streaming SSE. Acumula chunks
   * do modelo conforme chegam do backend. Resetado a cada nova tentativa
   * de apply. Quando streaming termina, `applyResult` é setado e
   * `streamingPrompt` vira irrelevante (mostra-se o diff).
   */
  const [streamingPrompt, setStreamingPrompt] = useState('');
  /** Flag indicando que o apply está em progresso via stream. */
  const [isStreamingApply, setIsStreamingApply] = useState(false);

  // ─── A/B Test state ───────────────────────────────────────────────────
  const [abSessionId, setAbSessionId] = useState<string | undefined>();

  // ─── Hooks ────────────────────────────────────────────────────────────
  const startTraining = useStartTraining(agentId);
  const { data: snapshot, error: snapshotError } = useSnapshot(
    snapshotId ?? undefined,
  );
  const generateSuggestions = useGenerateSuggestions(agentId);

  // ─── A/B Test hooks ─────────────────────────────────────────────────
  const startABTest = useStartABTest(agentId);
  const abTestSession = useABTestSession(agentId, abSessionId);
  const applyABVariant = useApplyABTestVariant(agentId);

  // ─── Transições ───────────────────────────────────────────────────────

  const resetState = useCallback(() => {
    setStep('choose-type');
    setSnapshotId(null);
    setSelectedSuggestionIds([]);
    setUserInstructions('');
    setSuggestions([]);
    setSuggestionsError(null);
    setApplyResult(null);
    setApplyError(null);
    setStreamingPrompt('');
    setIsStreamingApply(false);
    setAbSessionId(undefined);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  /**
   * Cliente escolheu tipo de treino → dispara POST /train e transiciona
   * pra step running. Em modo 'manual', backend marca snapshot como
   * completed imediatamente, o que faz o polling pegar já o estado final.
   */
  const handleSelectType = useCallback(
    async (type: TrainingScenarioType | 'compare'): Promise<void> => {
      if (type === 'compare') {
        setStep('ab-config');
        return;
      }

      try {
        const result = await startTraining.mutateAsync({ scenarioType: type });
        setSnapshotId(result.snapshotId);
        setStep(type === 'manual' ? 'results' : 'running');
      } catch (error) {
        // Deixa erro se propagar pro state do mutation
        void error;
      }
    },
    [startTraining],
  );

  /** Quando o snapshot termina (running → completed), avança pro Step 3. */
  useEffect(() => {
    if (step === 'running' && snapshot?.status === 'completed') {
      setStep('results');
    }
  }, [step, snapshot?.status]);

  /** Quando A/B test completa, avança para resultados. */
  useEffect(() => {
    if (step === 'ab-running' && abTestSession.data?.status === 'completed') {
      setStep('ab-results');
    }
  }, [step, abTestSession.data?.status]);

  /**
   * Auto-dispara geração de sugestões quando snapshot completa
   * (excluindo modo manual que não tem cenários).
   */
  const triggerGenerateSuggestions = useCallback(async (): Promise<void> => {
    if (!snapshotId) return;
    try {
      setSuggestionsError(null);
      const result = await generateSuggestions.mutateAsync({ snapshotId });
      setSuggestions(result.suggestions);
    } catch (error) {
      setSuggestionsError(error as Error);
    }
  }, [snapshotId, generateSuggestions]);

  /** Toggle de sugestão selecionada */
  const handleToggleSuggestion = useCallback((id: string) => {
    setSelectedSuggestionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  /**
   * Cliente clicou em "Aplicar e ver diff" — chama o endpoint streaming
   * SSE `/apply/stream`, acumula chunks no `streamingPrompt` e transiciona
   * pra step 'diff' quando o stream termina.
   */
  const handleApply = useCallback(async (): Promise<void> => {
    if (!agentId || !snapshotId) return;
    setApplyError(null);
    setStreamingPrompt('');
    setIsStreamingApply(true);

    try {
      const result = await agentTrainingApi.applyTrainingStream(
        agentId,
        {
          snapshotId,
          selectedSuggestionIds,
          ...(userInstructions.trim() ? { userInstructions } : {}),
        },
        (chunk) => {
          setStreamingPrompt((prev) => prev + chunk);
        },
      );

      setIsStreamingApply(false);

      if (!result.success) {
        setApplyError(
          result.error ||
            'Não foi possível gerar o novo prompt. Tente novamente.',
        );
        setStreamingPrompt('');
        return;
      }

      setApplyResult(result);
      setStep('diff');
    } catch (error) {
      setIsStreamingApply(false);
      setStreamingPrompt('');
      setApplyError(
        (error as Error)?.message ||
          'Erro inesperado ao aplicar o treinamento. Tente novamente.',
      );
    }
  }, [agentId, snapshotId, selectedSuggestionIds, userInstructions]);

  /** Cliente confirmou o diff → aplica no form do AgentFormPage e fecha */
  const handleConfirmDiff = useCallback(() => {
    if (applyResult?.newSystemPrompt && applyResult.snapshotId) {
      onApplyPrompt(applyResult.newSystemPrompt, applyResult.snapshotId);
    }
    resetState();
    onClose();
  }, [applyResult, onApplyPrompt, onClose, resetState]);

  /** Cliente rejeitou o diff → volta pro Step 3 */
  const handleRejectDiff = useCallback(() => {
    setApplyResult(null);
    setStep('results');
  }, []);

  /** Botão "voltar" no Step 3 → volta pro Step 1 */
  const handleBackToStart = useCallback(() => {
    resetState();
  }, [resetState]);

  // ─── Derivados ────────────────────────────────────────────────────────

  const modalTitle = useMemo(() => {
    switch (step) {
      case 'choose-type':
        return 'Treinar agente';
      case 'running':
        return 'Treinando agente...';
      case 'results':
        return 'Resultados do treino';
      case 'diff':
        return 'Revisar mudanças';
      case 'ab-config':
        return 'Comparar Modelos';
      case 'ab-running':
        return 'Comparando modelos...';
      case 'ab-results':
        return 'Resultados da comparação';
    }
  }, [step]);

  const showBackButton = useMemo(
    () =>
      step === 'results' ||
      step === 'running' ||
      step === 'ab-config' ||
      step === 'ab-running' ||
      step === 'ab-results',
    [step],
  );

  // ─── Render ───────────────────────────────────────────────────────────

  if (!isOpen) return null;

  // Step 4 = PromptDiffModal sobreposto (componente próprio com isOpen)
  if (step === 'diff' && applyResult) {
    return (
      <PromptDiffModal
        isOpen={true}
        onClose={handleRejectDiff}
        onConfirm={handleConfirmDiff}
        originalPrompt={applyResult.previousSystemPrompt}
        newPrompt={applyResult.newSystemPrompt}
        mode="apply"
      />
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="relative w-full sm:max-w-3xl rounded-t-2xl sm:rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        style={
          {
            backgroundColor: '#ffffff',
            maxHeight: '90vh',
            // Override de CSS variables pra tema claro fixo (regra do projeto)
            '--theme-text': '#1e293b',
            '--theme-muted-text': '#6b7280',
            '--theme-input-background': '#ffffff',
            '--theme-input-text': '#1e293b',
            '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
            '--theme-input-focus': '#8b5cf6',
            '--theme-card-background': '#ffffff',
            '--theme-card-text': '#334155',
            '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-card-muted': '#94a3b8',
            '--theme-primary': '#8b5cf6',
            '--theme-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-component-background': '#ffffff',
          } as React.CSSProperties
        }
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <div className="flex items-center gap-3">
            {showBackButton && (
              <button
                type="button"
                onClick={handleBackToStart}
                className="p-1 rounded hover:bg-slate-100 transition-colors"
                style={{ color: '#64748b' }}
                aria-label="Voltar"
              >
                <FiArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-slate-800">
              {modalTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded hover:bg-slate-100 transition-colors"
            style={{ color: '#64748b' }}
            aria-label="Fechar"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-5 flex-1">
          {step === 'choose-type' && (
            <TrainStep1_ChooseType
              onSelect={handleSelectType}
              allowCustom={false}
            />
          )}

          {step === 'running' && (
            <TrainStep2_Running
              snapshot={snapshot ?? null}
              error={snapshotError}
            />
          )}

          {step === 'results' && snapshot && (
            <TrainStep3_Results
              snapshot={snapshot}
              suggestions={suggestions}
              loadingSuggestions={generateSuggestions.isPending}
              suggestionsError={suggestionsError}
              selectedSuggestionIds={selectedSuggestionIds}
              userInstructions={userInstructions}
              onToggleSuggestion={handleToggleSuggestion}
              onUserInstructionsChange={setUserInstructions}
              onGenerateSuggestions={triggerGenerateSuggestions}
              onApply={handleApply}
              applying={isStreamingApply}
              applyError={applyError}
              streamingPrompt={streamingPrompt}
              isStreamingApply={isStreamingApply}
            />
          )}

          {step === 'ab-config' && (
            <ABTestConfigStep
              onStart={(variants: ABTestVariantInput[], scenarioType: 'quick' | 'full') => {
                startABTest.mutate({ variants, scenarioType }, {
                  onSuccess: (data) => {
                    setAbSessionId(data.sessionId);
                    setStep('ab-running');
                  },
                });
              }}
              onBack={() => setStep('choose-type')}
              isStarting={startABTest.isPending}
            />
          )}

          {step === 'ab-running' && (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}>
                <FiLoader size={32} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
              </div>
              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '8px' }}>
                Comparando modelos...
              </div>
              {abTestSession.data && (
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  {abTestSession.data.variants.filter((v) => v.status === 'completed').length} de{' '}
                  {abTestSession.data.variants.length} variantes concluídas
                </div>
              )}
            </div>
          )}

          {step === 'ab-results' && abTestSession.data && (
            <ABTestResultsTable
              session={abTestSession.data}
              onApply={(variantId) => {
                applyABVariant.mutate({ sessionId: abSessionId!, variantId }, {
                  onSuccess: () => {
                    handleClose();
                  },
                });
              }}
              onClose={handleClose}
              isApplying={applyABVariant.isPending}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
